import { Component, ElementRef, HostListener, computed, inject, input, output, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

const I18N = 'shared.chartToolbar';

export type ChartCardView = 'chart' | 'table';
/** Formatos oferecidos no menu — imagem do gráfico ou dados da tabela. */
export type ChartExportRequest = 'svg' | 'png' | 'csv' | 'xlsx' | 'metadata-pdf' | 'metadata-txt';

/** O que está sendo exibido — define o rótulo e os formatos oferecidos no menu. */
export type ChartToolbarContext = 'chart' | 'table' | 'map' | 'metadata';

interface ExportOption {
  format: ChartExportRequest;
  labelKey: string;
}

const IMAGE_OPTIONS: ExportOption[] = [
  { format: 'svg', labelKey: 'svg' },
  { format: 'png', labelKey: 'png' },
];

const EXPORT_BY_CONTEXT: Record<ChartToolbarContext, { labelKey: string; options: ExportOption[] }> = {
  chart: { labelKey: 'exportChart', options: IMAGE_OPTIONS },
  map: { labelKey: 'exportMap', options: IMAGE_OPTIONS },
  table: {
    labelKey: 'exportTable',
    options: [
      { format: 'xlsx', labelKey: 'xlsx' },
      { format: 'csv', labelKey: 'csv' },
    ],
  },
  // Metadados são para leitura, não para planilha.
  metadata: {
    labelKey: 'exportMetadata',
    options: [
      { format: 'metadata-pdf', labelKey: 'pdf' },
      { format: 'metadata-txt', labelKey: 'txt' },
    ],
  },
};

/**
 * Barra de ações compartilhada pelas telas de visualização: alterna gráfico/tabela e
 * concentra os exports. O DataCuidados usa só o menu de export (tem abas próprias, que
 * incluem Mapa), então o toggle é opcional.
 */
@Component({
  selector: 'app-chart-toolbar',
  standalone: true,
  imports: [TranslocoPipe],
  host: { class: 'inline-flex flex-wrap items-center justify-end gap-2' },
  template: `
    <!-- Conteúdo solto entra antes do toggle; use [data-toolbar-end] para ficar após o export. -->
    <ng-content />

    @if (showViewToggle()) {
      <div
        role="group"
        [attr.aria-label]="i18n + '.viewGroup' | transloco"
        class="inline-flex h-9 items-center rounded-full border border-border bg-muted/60 p-0.5"
      >
        <button
          type="button"
          (click)="viewChange.emit('chart')"
          [attr.aria-pressed]="view() === 'chart'"
          class="inline-flex h-full items-center gap-1.5 rounded-full px-3 text-xs font-medium transition"
          [class]="view() === 'chart' ? 'bg-card shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'"
          [style.color]="view() === 'chart' ? accent() : null"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
            <path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" />
          </svg>
          <span class="hidden sm:inline">{{ i18n + '.viewChart' | transloco }}</span>
        </button>
        <button
          type="button"
          (click)="viewChange.emit('table')"
          [attr.aria-pressed]="view() === 'table'"
          [disabled]="!canExportTable()"
          class="inline-flex h-full items-center gap-1.5 rounded-full px-3 text-xs font-medium transition disabled:opacity-40"
          [class]="view() === 'table' ? 'bg-card shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'"
          [style.color]="view() === 'table' ? accent() : null"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
            <path d="M12 3v18" /><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M3 9h18" /><path d="M3 15h18" />
          </svg>
          <span class="hidden sm:inline">{{ i18n + '.viewTable' | transloco }}</span>
        </button>
      </div>
    }

    <div class="relative">
      <button
        type="button"
        (click)="open.set(!open())"
        [disabled]="!hasAnyExport() || busy()"
        [attr.aria-expanded]="open()"
        [attr.aria-haspopup]="'menu'"
        class="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3.5 text-xs font-medium text-muted-foreground transition hover:text-foreground disabled:opacity-40"
      >
        @if (busy()) {
          <span class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
        } @else {
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" />
          </svg>
        }
        <span class="hidden sm:inline">{{ i18n + '.' + exportLabelKey() | transloco }}</span>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 transition" [class.rotate-180]="open()">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      @if (open()) {
        <div
          role="menu"
          class="absolute right-0 top-full z-40 mt-1 w-52 overflow-hidden rounded-xl border border-border bg-card py-1 shadow-lg"
        >
          @for (option of exportOptions(); track option.format) {
            <button
              type="button"
              role="menuitem"
              (click)="request(option.format)"
              class="block w-full px-3 py-1.5 text-left text-xs text-foreground transition hover:bg-muted/60"
            >
              {{ i18n + '.' + option.labelKey | transloco }}
            </button>
          }
        </div>
      }
    </div>

    @if (showShare()) {
      <button
        type="button"
        (click)="share()"
        [attr.aria-label]="i18n + '.share' | transloco"
        class="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3.5 text-xs font-medium text-muted-foreground transition hover:border-primary/40 hover:text-primary"
      >
        @if (shareCopied()) {
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        } @else {
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <path d="m8.59 13.51 6.83 3.98" /><path d="m15.41 6.51-6.82 3.98" />
          </svg>
        }
        <span class="hidden sm:inline">
          {{ i18n + (shareCopied() ? '.shareCopied' : '.share') | transloco }}
        </span>
      </button>
    }

    <ng-content select="[data-toolbar-end]" />

    @if (showFullscreen()) {
      <button
        type="button"
        (click)="fullscreenToggle.emit()"
        [attr.aria-pressed]="fullscreen()"
        [attr.aria-label]="i18n + (fullscreen() ? '.exitFullscreen' : '.fullscreen') | transloco"
        [attr.title]="i18n + (fullscreen() ? '.exitFullscreen' : '.fullscreen') | transloco"
        class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition hover:text-foreground"
        [style.color]="fullscreen() ? accent() : null"
        [style.border-color]="fullscreen() ? accent() : null"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
          @if (fullscreen()) {
            <path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" />
            <path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />
          } @else {
            <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" />
            <path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
          }
        </svg>
      </button>
    }
  `,
})
export class ChartToolbarComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly view = input<ChartCardView>('chart');
  readonly showViewToggle = input(true);
  /** O que está na tela agora; o menu oferece só os formatos que fazem sentido para isso. */
  readonly context = input<ChartToolbarContext>('chart');
  readonly canExportChart = input(true);
  readonly canExportTable = input(true);
  readonly canExportMetadata = input(false);
  readonly busy = input(false);
  readonly accent = input<string | null>(null);
  /** Compartilhar o endereço atual — a URL já carrega indicador, filtros e recorte. */
  readonly showShare = input(false);
  readonly shareTitle = input('');
  readonly shareUrl = input('');
  /** Botão de tela cheia; quem controla o estado é a tela, que sabe o que maximizar. */
  readonly showFullscreen = input(false);
  readonly fullscreen = input(false);

  readonly viewChange = output<ChartCardView>();
  readonly exportRequest = output<ChartExportRequest>();
  readonly fullscreenToggle = output<void>();

  protected readonly i18n = I18N;
  protected readonly open = signal(false);
  protected readonly shareCopied = signal(false);
  protected readonly exportLabelKey = computed(() => EXPORT_BY_CONTEXT[this.context()].labelKey);
  protected readonly exportOptions = computed(() => EXPORT_BY_CONTEXT[this.context()].options);

  /** Só habilita quando a tela realmente tem o conteúdo daquele contexto. */
  protected readonly hasAnyExport = computed(() => {
    switch (this.context()) {
      case 'chart':
      case 'map':
        return this.canExportChart();
      case 'metadata':
        return this.canExportMetadata();
      default:
        return this.canExportTable();
    }
  });

  protected request(format: ChartExportRequest): void {
    this.open.set(false);
    this.exportRequest.emit(format);
  }

  /** Usa o menu nativo quando existe; senão copia o link e confirma no próprio botão. */
  protected async share(): Promise<void> {
    if (typeof window === 'undefined') return;
    const url = this.shareUrl() || window.location.href;
    const title = this.shareTitle() || document.title;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      this.shareCopied.set(true);
      setTimeout(() => this.shareCopied.set(false), 2000);
    } catch {
      /* cancelado pelo usuário ou clipboard indisponível */
    }
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.open.set(false);
  }
}
